/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ErrorTypes } from "../error";
import { createActionErrorMapper } from "./ActionErrorMapper";
import type { ActionErrorMapper } from "./ActionErrorMapper";

/**
 * One mapper per Schedule Actions family — Recording, Bookmark, OnePass —
 * the three action groups behind Content Detail's "Schedule Actions" row
 * (record/modify/cancel/delete/recover a single-offer recording, add/remove
 * a bookmark, and create/modify/cancel a OnePass). Kept in one place, not
 * under `viewModel/contentDetail`, because OnePass's own API calls live in
 * `viewModel/myHub/seriesRecordingOptions` — this module has no single
 * feature owner, any of them can import it.
 *
 * Each mapper is a distinct catalog code, not the screen-level A716
 * (`ContentDetailErrorMapper`) — one code per action family, not per screen.
 */

/**
 * Catalog code for a recording-lifecycle action failing: create (the Record
 * pill, wired — see `createContentDetailRecording`), or modify/cancel/
 * delete/recover a single-offer recording (not wired yet).
 */
export const RECORDING_ACTION_ERROR_CODE: string =
  ErrorTypes.RECORDING_ACTION_ERROR_R323;

/** Maps a raw error from a recording-lifecycle action to R323. */
export const recordingActionErrorMapper: ActionErrorMapper =
  createActionErrorMapper(RECORDING_ACTION_ERROR_CODE);

/**
 * Catalog code for a bookmark/favorite action failing (add/remove favorite).
 * Not wired to anything yet — no bookmark interactor exists in the codebase
 * at all yet, so nothing calls this mapper today.
 */
export const BOOKMARK_ACTION_ERROR_CODE: string =
  ErrorTypes.BOOKMARK_ACTION_ERROR_R324;

/** Maps a raw error from a bookmark/favorite action to R324. */
export const bookmarkActionErrorMapper: ActionErrorMapper =
  createActionErrorMapper(BOOKMARK_ACTION_ERROR_CODE);

/**
 * Catalog code for a OnePass write failing: creating/updating OnePass to save
 * the Series Recording Options overlay (wired — see
 * `seriesRecordingOptionsThunks.ts`), or cancelling one (My Hub's
 * `cancelSeriesRecordings`). Loading the overlay's options is R506, below.
 */
export const ONEPASS_ACTION_ERROR_CODE: string =
  ErrorTypes.ONEPASS_ACTION_ERROR_R325;

/**
 * Maps a raw error from a OnePass write to R325. Applied inside
 * `seriesRecordingOptionsThunks.ts` itself, so every caller of
 * `saveSeriesRecordingOptions` gets the coded toast for free rather than each
 * call site mapping the error again.
 */
export const onePassActionErrorMapper: ActionErrorMapper =
  createActionErrorMapper(ONEPASS_ACTION_ERROR_CODE);

/**
 * Catalog code for fetching the schedule/options that open the Series
 * Recording Options overlay (UAN-4717) — split out of R325, which now covers
 * only the writes.
 */
export const SERIES_RECORDING_OPTIONS_LOAD_ERROR_CODE: string =
  ErrorTypes.SERIES_RECORDING_OPTIONS_LOAD_FAILED_R506;

/**
 * Maps a raw error from loading series recording options to R506. Applied
 * inside `loadSeriesRecordingOptions`, for every surface that opens the
 * overlay (My Hub, Content Detail, the player).
 */
export const seriesRecordingOptionsLoadErrorMapper: ActionErrorMapper =
  createActionErrorMapper(SERIES_RECORDING_OPTIONS_LOAD_ERROR_CODE);
